<script setup lang="ts">
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
  type Plugin,
} from 'chart.js'
import { Line } from 'vue-chartjs'
import 'chartjs-adapter-moment'
import { computed } from 'vue'

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Filler)

const props = defineProps<{
  points: { period_date: string; value_amount: number }[]
  currency?: string
}>()

const LINE = '#3f7d7d'
const END_FILL = '#6cae5e'
const END_HALO = 'rgba(108, 174, 94, 0.28)'

const fmt = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.currency ?? 'USD',
    maximumFractionDigits: 0,
  }).format(v)

const lastIndex = computed(() => props.points.length - 1)

const chartData = computed(() => ({
  datasets: [
    {
      label: 'Current Value',
      data: props.points.map((p) => ({ x: p.period_date, y: p.value_amount })),
      borderColor: LINE,
      borderWidth: 2.5,
      cubicInterpolationMode: 'monotone' as const,
      tension: 0.4,
      fill: true,
      backgroundColor: (ctx: any) => {
        const { chart } = ctx
        const { ctx: c, chartArea } = chart
        if (!chartArea) return 'rgba(120, 165, 150, 0)'
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        g.addColorStop(0, 'rgba(120, 165, 150, 0.30)')
        g.addColorStop(0.6, 'rgba(120, 165, 150, 0.08)')
        g.addColorStop(1, 'rgba(120, 165, 150, 0)')
        return g
      },
      pointRadius: props.points.map((_, i) => (i === 0 || i === lastIndex.value ? 5 : 0)),
      pointHoverRadius: props.points.map((_, i) => (i === 0 || i === lastIndex.value ? 6 : 3)),
      pointBackgroundColor: props.points.map((_, i) => (i === lastIndex.value ? END_FILL : '#ffffff')),
      pointBorderColor: props.points.map((_, i) => (i === lastIndex.value ? END_HALO : LINE)),
      pointBorderWidth: props.points.map((_, i) => (i === lastIndex.value ? 6 : i === 0 ? 2 : 0)),
    },
  ],
}))

const dashedGrid: Plugin = {
  id: 'dashedGrid',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart
    const y = scales.y as any
    if (!y || !chartArea) return
    ctx.save()
    ctx.setLineDash([4, 4])
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(15, 23, 42, 0.07)'
    for (const tick of y.ticks) {
      const yPos = y.getPixelForValue(tick.value)
      ctx.beginPath()
      ctx.moveTo(chartArea.left, yPos)
      ctx.lineTo(chartArea.right, yPos)
      ctx.stroke()
    }
    ctx.restore()
  },
}

const options = computed(
  () =>
    ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 16, bottom: 4 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: { label: (c: any) => fmt(c.parsed.y) },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: { unit: 'month', displayFormats: { month: 'MMM [’]YY' }, tooltipFormat: 'MMM D, YYYY' },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#94a3b8',
            font: { size: 12 },
            maxRotation: 0,
            autoSkip: true,
            autoSkipPadding: 24,
          },
        },
        y: {
          grace: '15%',
          grid: { display: false },
          border: { display: false },
          ticks: { display: false, maxTicksLimit: 4 },
        },
      },
    }) as any,
)
</script>

<template>
  <div class="h-[300px] w-full">
    <Line :data="chartData" :options="options" :plugins="[dashedGrid]" />
  </div>
</template>
